import { useState, type FormEvent } from "react";

import { prettifyApiError } from "../../../shared/api";
import { urlPdf, type Correzione, type FatturaEstera } from "../fattureEstereApi";

/** I paesi da cui fatturano oggi i nostri fornitori esteri. Un codice che
 *  manca qui si mostra com'e': meglio «LU» che un nome sbagliato. */
const NOME_PAESE: Record<string, string> = {
  IE: "Irlanda",
  CY: "Cipro",
  US: "Stati Uniti",
  FR: "Francia",
  LU: "Lussemburgo",
  DE: "Germania",
  NL: "Paesi Bassi",
  GB: "Regno Unito",
  ES: "Spagna",
};

const giorno = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const quando = (iso: string) =>
  new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export function importoLeggibile(importo: string | null, valuta: string | null): string | null {
  if (importo === null) return null;
  const n = Number(importo);
  if (!Number.isFinite(n)) return importo;
  const cifra = n.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return valuta ? `${cifra} ${valuta}` : cifra;
}

/** Il nome del PDF senza estensione e senza il prefisso ripetuto dei fornitori
 *  («Invoice-1234.pdf» → «Invoice-1234»): basta a distinguere fattura e
 *  ricevuta quando la mail ne porta due. */
function etichettaPdf(nome: string, quanti: number): string {
  if (quanti === 1) return "PDF";
  return nome.replace(/\.pdf$/i, "").slice(0, 24);
}

function IconaSpunta() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4 fill-none stroke-current stroke-[2.4] [stroke-linecap:round] [stroke-linejoin:round]"
    >
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

function IconaDocumento() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-[14px] w-[14px] fill-none stroke-current stroke-[1.7] [stroke-linecap:round] [stroke-linejoin:round]"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

function IconaEsterna() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-[13px] w-[13px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
    >
      <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

const TASTO_PICCOLO =
  "inline-flex h-[28px] items-center gap-1.5 rounded-full border border-line-2 bg-card px-2.5 text-[12px] font-semibold text-ink transition-colors hover:border-ink-3 hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-10 max-md:px-3.5 max-md:text-[13.5px]";

const CAMPO =
  "h-[32px] w-full rounded-md border border-line-2 bg-card px-2.5 text-[13px] text-ink outline-none focus:border-ink-3 focus:shadow-[0_0_0_3px_rgba(31,27,22,0.06)] max-md:h-11 max-md:text-[16px]";

type Props = {
  fattura: FatturaEstera;
  puoScrivere: boolean;
  titoloSenzaPermesso?: string;
  onSpunta: (fattura: FatturaEstera, fatta: boolean) => Promise<void>;
  onCorreggi: (fattura: FatturaEstera, corpo: Correzione) => Promise<void>;
  onElimina: (fattura: FatturaEstera) => Promise<void>;
  onCreaAut: (fattura: FatturaEstera) => Promise<void>;
};

export function FatturaEsteraRiga({
  fattura,
  puoScrivere,
  titoloSenzaPermesso,
  onSpunta,
  onCorreggi,
  onElimina,
  onCreaAut,
}: Props) {
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [correggo, setCorreggo] = useState(false);
  const fatta = fattura.autofattura_il !== null;
  const certo = importoLeggibile(fattura.importo, fattura.valuta);
  const letto = certo ? null : importoLeggibile(fattura.importo_letto, fattura.valuta);
  const importo = certo ?? (letto ? `${letto}*` : null);
  const paese = fattura.paese ? (NOME_PAESE[fattura.paese] ?? fattura.paese) : null;

  async function esegui(azione: () => Promise<void>) {
    setInCorso(true);
    setErrore(null);
    try {
      await azione();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setInCorso(false);
    }
  }

  async function salva(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const dati = new FormData(e.currentTarget);
    const testo = (k: string) => {
      const v = dati.get(k);
      return typeof v === "string" ? v.trim() : "";
    };
    const corpo: Correzione = { numero: testo("numero"), nota: testo("nota") };
    if (testo("data_documento")) corpo.data_documento = testo("data_documento");
    if (testo("importo")) corpo.importo = testo("importo").replace(",", ".");
    if (testo("valuta")) corpo.valuta = testo("valuta");
    await esegui(async () => {
      await onCorreggi(fattura, corpo);
      setCorreggo(false);
    });
  }

  return (
    <li
      className={[
        "group/riga px-4 py-3 transition-colors max-md:px-3",
        fatta ? "bg-paper/60" : "hover:bg-paper/50",
      ].join(" ")}
    >
      <div className="grid grid-cols-[32px_minmax(0,1.2fr)_minmax(0,1fr)_96px_120px_minmax(0,320px)] items-center gap-x-4 max-lg:grid-cols-[32px_minmax(0,1fr)_auto] max-lg:gap-y-1.5">
        <button
          type="button"
          aria-pressed={fatta}
          aria-label={
            fatta
              ? `Autofattura fatta per ${fattura.fornitore_nome}: togli la spunta`
              : `Segna autofattura fatta per ${fattura.fornitore_nome}`
          }
          title={puoScrivere ? undefined : titoloSenzaPermesso}
          disabled={!puoScrivere || inCorso}
          onClick={() => void esegui(() => onSpunta(fattura, !fatta))}
          className={[
            "flex h-8 w-8 items-center justify-center rounded-full border-2 transition-[background-color,border-color,transform] duration-150 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-1 disabled:cursor-not-allowed max-md:h-10 max-md:w-10 max-lg:row-span-2 max-lg:self-start",
            fatta
              ? "border-green bg-green text-white"
              : "border-line-2 bg-card text-transparent hover:border-green hover:text-green/60",
            inCorso ? "opacity-60" : "",
          ].join(" ")}
        >
          <IconaSpunta />
        </button>

        <div className="min-w-0">
          <p className="truncate text-[14px] font-bold text-ink">{fattura.fornitore_nome}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-ink-2">
            {paese ? (
              <span>
                {paese} <span className="text-ink-3">({fattura.paese})</span>
              </span>
            ) : (
              <span className="font-semibold text-amber" title="Il fornitore fattura da piu' paesi">
                Paese: guarda il PDF
              </span>
            )}
            {fattura.nota_di_credito && (
              <span className="rounded-full border border-line-2 px-2 py-px text-[11px] font-bold text-ink">
                Nota di credito
              </span>
            )}
            {!fattura.mittente_verificato && (
              <span
                className="rounded-full border border-red/40 bg-red-soft px-2 py-px text-[11px] font-bold text-ink"
                title={`La mail da ${fattura.mittente} non ha la firma del suo dominio: controlla che sia vera prima dell'autofattura`}
              >
                Mittente non verificato
              </span>
            )}
            {fattura.origine === "manuale" && (
              <span className="text-ink-3">Caricata a mano da {fattura.caricata_da || "?"}</span>
            )}
            {fattura.origine === "portale" && (
              <span className="text-ink-3">Letta da OM sul portale</span>
            )}
          </p>
        </div>

        <div className="min-w-0 max-lg:col-start-2">
          <p className="truncate font-mono text-[12.5px] text-ink">
            {fattura.numero ?? <span className="font-sans text-ink-3">Numero sul PDF</span>}
          </p>
          {fattura.oggetto && (
            <p className="truncate text-[11.5px] text-ink-3" title={fattura.oggetto}>
              {fattura.oggetto}
            </p>
          )}
          {fattura.nota && (
            <p className="truncate text-[11.5px] text-ink-2" title={fattura.nota}>
              Nota: {fattura.nota}
            </p>
          )}
        </div>

        <p className="text-[13px] tabular-nums text-ink max-lg:col-start-2 max-lg:inline max-lg:text-[12.5px] max-lg:text-ink-2">
          {giorno(fattura.data_documento)}
          {importo && <span className="hidden max-lg:inline"> · {importo}</span>}
        </p>

        <p className="text-right text-[13px] font-semibold tabular-nums text-ink max-lg:hidden">
          {certo ??
            (letto ? (
              <span
                className="font-normal text-ink-2"
                title="Letto dal PDF: controllalo sulla fattura prima di usarlo"
              >
                {letto}*
              </span>
            ) : (
              <span className="font-normal text-ink-3">—</span>
            ))}
        </p>

        <div className="flex flex-wrap items-center justify-end gap-1.5 max-lg:col-span-2 max-lg:col-start-2 max-lg:justify-start">
          {fattura.pdf.map((p) => (
            <a
              key={p.id}
              href={urlPdf(p.id)}
              target="_blank"
              rel="noopener noreferrer"
              className={TASTO_PICCOLO}
              title={`Apri ${p.nome}`}
            >
              <IconaDocumento />
              {etichettaPdf(p.nome, fattura.pdf.length)}
            </a>
          ))}
          {fattura.pdf.length === 0 && fattura.portale && (
            <a
              href={fattura.portale}
              target="_blank"
              rel="noopener noreferrer"
              className={TASTO_PICCOLO}
              title="Questa fattura non ha il PDF in allegato: si scarica dal portale"
            >
              Dal portale
              <IconaEsterna />
            </a>
          )}
          {fattura.pdf.length === 0 && !fattura.portale && (
            <span className="text-[12px] text-ink-3">Senza PDF</span>
          )}
          {!fatta && (
            <button
              type="button"
              className="inline-flex h-[28px] items-center gap-1.5 rounded-full bg-ink px-3 text-[12px] font-semibold text-paper transition-[background-color,transform] hover:bg-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-1 active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-line-2 disabled:text-ink-3 max-md:h-10 max-md:px-3.5 max-md:text-[13.5px]"
              disabled={!puoScrivere || inCorso || fattura.aut_crea.motivo !== null}
              title={
                !puoScrivere
                  ? titoloSenzaPermesso
                  : (fattura.aut_crea.motivo ??
                    "Crea l'autofattura in Eureka, uguale all'ultima di questo fornitore")
              }
              onClick={() => {
                const conferma =
                  `Creo in Eureka l'autofattura di ${fattura.fornitore_nome}` +
                  `${fattura.numero ? `, documento ${fattura.numero}` : ""}` +
                  `${certo ? `, imponibile ${certo}` : ""}` +
                  `${letto ? `, importo LETTO dal PDF e non controllato: ${letto}` : ""}.\n\n` +
                  (fattura.pdf.length > 0
                    ? "In Eureka un documento non si cancella: controlla il PDF prima. Procedo?"
                    : "Il portale non da' il PDF di questo documento. In Eureka un documento " +
                      "non si cancella: controlla numero e importo. Procedo?");
                if (window.confirm(conferma)) void esegui(() => onCreaAut(fattura));
              }}
            >
              {inCorso ? "Creo…" : "Crea in Eureka"}
            </button>
          )}
          <button
            type="button"
            className={TASTO_PICCOLO}
            aria-expanded={correggo}
            disabled={!puoScrivere}
            title={puoScrivere ? "Correggi numero, data o importo" : titoloSenzaPermesso}
            onClick={() => setCorreggo((v) => !v)}
          >
            Correggi
          </button>
          {fattura.origine === "manuale" && !fatta && (
            <button
              type="button"
              className={`${TASTO_PICCOLO} hover:!border-red hover:!text-red`}
              disabled={!puoScrivere || inCorso}
              title={puoScrivere ? "Togli questa fattura caricata a mano" : titoloSenzaPermesso}
              onClick={() => {
                if (window.confirm(`Togliere la fattura ${fattura.fornitore_nome}?`)) {
                  void esegui(() => onElimina(fattura));
                }
              }}
            >
              Togli
            </button>
          )}
        </div>
      </div>

      {fatta && fattura.autofattura_il && (
        <p className="mt-1 pl-12 text-[12px] font-semibold text-green max-md:pl-[52px]">
          {fattura.eureka_aut ? (
            <>
              In Eureka: AUT {fattura.eureka_aut.numero}
              {fattura.eureka_aut.data ? ` del ${giorno(fattura.eureka_aut.data)}` : ""}
              <span className="font-normal text-ink-2">
                {fattura.autofattura_da === "Eureka"
                  ? " · spuntata da OM"
                  : ` · creata da OM per ${fattura.autofattura_da ?? "?"}`}
              </span>
            </>
          ) : (
            <>
              Autofattura fatta{fattura.autofattura_da ? ` da ${fattura.autofattura_da}` : ""} il{" "}
              {quando(fattura.autofattura_il)}
            </>
          )}
        </p>
      )}

      {!fatta && fattura.aut_crea.errore && (
        <p
          role="alert"
          className="mt-1.5 pl-12 text-[12.5px] font-semibold text-red max-md:pl-[52px]"
        >
          {fattura.aut_crea.errore}
        </p>
      )}

      {errore && (
        <p
          role="alert"
          className="mt-1.5 pl-12 text-[12.5px] font-semibold text-red max-md:pl-[52px]"
        >
          {errore}
        </p>
      )}

      {correggo && (
        <form
          onSubmit={(e) => void salva(e)}
          className="mt-3 ml-12 grid grid-cols-[1.2fr_1fr_1fr_72px] gap-2.5 rounded-md border border-line bg-paper p-3 max-md:ml-0 max-md:grid-cols-2"
        >
          <label className="text-[11.5px] font-semibold text-ink-2">
            Numero
            <input
              name="numero"
              defaultValue={fattura.numero ?? ""}
              maxLength={80}
              className={`${CAMPO} mt-1`}
            />
          </label>
          <label className="text-[11.5px] font-semibold text-ink-2">
            Data
            <input
              name="data_documento"
              type="date"
              defaultValue={fattura.data_documento.slice(0, 10)}
              className={`${CAMPO} mt-1`}
            />
          </label>
          <label className="text-[11.5px] font-semibold text-ink-2">
            Importo
            <input
              name="importo"
              inputMode="decimal"
              pattern="[0-9]+([.,][0-9]{1,2})?"
              defaultValue={fattura.importo ?? ""}
              className={`${CAMPO} mt-1 tabular-nums`}
            />
          </label>
          <label className="text-[11.5px] font-semibold text-ink-2">
            Valuta
            <input
              name="valuta"
              maxLength={3}
              defaultValue={fattura.valuta ?? "EUR"}
              className={`${CAMPO} mt-1 uppercase`}
            />
          </label>
          <label className="col-span-4 text-[11.5px] font-semibold text-ink-2 max-md:col-span-2">
            Nota per Annalisa
            <input
              name="nota"
              defaultValue={fattura.nota ?? ""}
              maxLength={500}
              className={`${CAMPO} mt-1`}
            />
          </label>
          <div className="col-span-4 flex items-center justify-end gap-2 max-md:col-span-2">
            <button
              type="submit"
              disabled={inCorso}
              className="h-[32px] rounded-full bg-ink px-3.5 text-[12.5px] font-bold text-on-ink transition-opacity hover:opacity-90 disabled:opacity-50 max-md:h-11 max-md:flex-1"
            >
              {inCorso ? "Salvo…" : "Salva"}
            </button>
            <button
              type="button"
              onClick={() => setCorreggo(false)}
              className="h-[32px] rounded-full px-2 text-[12.5px] font-semibold text-ink-2 hover:text-ink max-md:h-11"
            >
              Annulla
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
